import React, { useState, useEffect, useMemo, useRef } from 'react';
import type {
  BusinessInfo,
  PurchaseOrder,
  PurchaseOrderItem,
  PurchaseOrderStatus,
  PurchasingInvoiceDefaults,
  PoGstMode,
  Supplier,
  InventoryItem,
} from '../../types';
import PlusIcon from '../icons/PlusIcon';
import TrashIcon from '../icons/TrashIcon';
import XCircleIcon from '../icons/XCircleIcon';
import POPasteImportPanel from './POPasteImportPanel';
import { newPoLine, poOrderTotal } from '../../lib/poUtils';
import { computePoTotals } from '../../lib/nzGst';
import {
  generatePurchaseOrderPdf,
  buildPurchaseOrderMailto,
} from '../../lib/purchaseOrderPdf';
import {
  hasBlockingIssues,
  validatePoPrint,
} from '../../lib/purchasingInvoiceValidation';

interface PurchaseOrderBuilderProps {
  po: PurchaseOrder | null;
  suppliers: Supplier[];
  inventoryItems: InventoryItem[];
  lastOrderForSupplier?: PurchaseOrder | null;
  initialSupplierId?: number;
  businessInfo?: BusinessInfo | null;
  purchasingDefaults?: PurchasingInvoiceDefaults;
  onClose: () => void;
  onSave: (po: PurchaseOrder) => void | Promise<void>;
}

const statuses: PurchaseOrderStatus[] = ['draft', 'ordered', 'received', 'cancelled'];
const UNITS: InventoryItem['unit'][] = ['kg', 'g', 'L', 'ml', 'units'];

const PurchaseOrderBuilder: React.FC<PurchaseOrderBuilderProps> = ({
  po,
  suppliers,
  inventoryItems,
  lastOrderForSupplier,
  initialSupplierId,
  businessInfo,
  purchasingDefaults,
  onClose,
  onSave,
}) => {
  const defaultGstMode = purchasingDefaults?.defaultGstMode ?? 'inclusive';
  const [supplierId, setSupplierId] = useState(suppliers[0]?.id || 0);
  const [status, setStatus] = useState<PurchaseOrderStatus>('draft');
  const [notes, setNotes] = useState('');
  const [gstMode, setGstMode] = useState<PoGstMode>(defaultGstMode);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [items, setItems] = useState<PurchaseOrderItem[]>([]);
  const [entryMode, setEntryMode] = useState<'quick' | 'paste'>('quick');
  const [saving, setSaving] = useState(false);
  const [highlightLineId, setHighlightLineId] = useState<string | null>(null);

  const [quickName, setQuickName] = useState('');
  const [quickQty, setQuickQty] = useState('1');
  const [quickCost, setQuickCost] = useState('');
  const [quickUnit, setQuickUnit] = useState<InventoryItem['unit']>('units');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const quickNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (po) {
      setSupplierId(po.supplierId);
      setStatus(po.status);
      setNotes(po.notes ?? '');
      setGstMode(po.gstMode ?? defaultGstMode);
      setPaymentTerms(po.paymentTerms ?? '');
      setDeliveryAddress(po.deliveryAddress ?? businessInfo?.address ?? '');
      setItems(po.items.length > 0 ? po.items : []);
    } else {
      setSupplierId(initialSupplierId ?? suppliers[0]?.id ?? 0);
      setStatus('draft');
      setNotes('');
      setGstMode(defaultGstMode);
      setPaymentTerms('');
      setDeliveryAddress(businessInfo?.address ?? '');
      setItems([]);
    }
  }, [po, suppliers, initialSupplierId, defaultGstMode, businessInfo?.address]);

  const selectedSupplier = useMemo(
    () => suppliers.find((s) => s.id === supplierId),
    [suppliers, supplierId],
  );

  useEffect(() => {
    if (po) return;
    if (!paymentTerms && selectedSupplier?.paymentTerms) {
      setPaymentTerms(selectedSupplier.paymentTerms);
    }
    if (!paymentTerms && purchasingDefaults?.defaultPaymentTerms) {
      setPaymentTerms(purchasingDefaults.defaultPaymentTerms);
    }
  }, [po, selectedSupplier, purchasingDefaults, paymentTerms]);

  const suggestions = useMemo(() => {
    const q = quickName.trim().toLowerCase();
    if (!q) return inventoryItems.slice(0, 8);
    return inventoryItems
      .filter((i) => i.name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [quickName, inventoryItems]);

  const totals = useMemo(() => computePoTotals(items, gstMode), [items, gstMode]);
  const total = poOrderTotal(items, gstMode);

  const draftPoForPrint = useMemo((): PurchaseOrder | null => {
    if (!po?.id || !selectedSupplier) return null;
    return {
      ...po,
      supplierId,
      status,
      items,
      notes: notes.trim() || undefined,
      gstMode,
      paymentTerms: paymentTerms.trim() || undefined,
      deliveryAddress: deliveryAddress.trim() || undefined,
    };
  }, [po, supplierId, status, items, notes, gstMode, paymentTerms, deliveryAddress, selectedSupplier]);

  const canPrintPo = status === 'ordered' || status === 'received';

  const handlePrintPo = () => {
    if (!draftPoForPrint || !selectedSupplier || !businessInfo) return;
    const issues = validatePoPrint(draftPoForPrint, businessInfo);
    if (hasBlockingIssues(issues)) {
      alert(issues.filter((i) => i.level === 'error').map((i) => i.message).join('\n'));
      return;
    }
    generatePurchaseOrderPdf(
      { po: draftPoForPrint, supplier: selectedSupplier, businessInfo, defaults: purchasingDefaults },
    );
  };

  const handleEmailPo = () => {
    if (!draftPoForPrint || !selectedSupplier) return;
    handlePrintPo();
    window.location.href = buildPurchaseOrderMailto(selectedSupplier, draftPoForPrint);
  };

  const commitQuickLine = (pick?: InventoryItem, asNewName?: string) => {
    const name = asNewName ?? pick?.name ?? quickName.trim();
    if (!name) return;
    const qty = Math.max(1, parseFloat(quickQty) || 1);
    const cost = parseFloat(quickCost) || pick?.costPerUnit || 0;
    const line = newPoLine({
      productName: name,
      quantity: qty,
      costPerUnit: cost,
      unit: pick?.unit ?? quickUnit,
      category: pick?.category,
      inventoryItemId: pick?.id,
    });
    setItems((prev) => [...prev, line]);
    setHighlightLineId(line.lineId);
    setTimeout(() => setHighlightLineId(null), 600);
    setQuickName('');
    setQuickQty('1');
    setQuickCost('');
    setShowSuggestions(false);
    quickNameRef.current?.focus();
  };

  const handleQuickKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitQuickLine();
    }
  };

  const updateLine = (index: number, patch: Partial<PurchaseOrderItem>) => {
    setItems((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeLine = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const duplicateLastOrder = () => {
    if (!lastOrderForSupplier?.items.length) return;
    const cloned = lastOrderForSupplier.items.map((line) =>
      newPoLine({
        productName: line.productName,
        quantity: line.quantity,
        costPerUnit: line.costPerUnit,
        unit: line.unit,
        category: line.category,
        inventoryItemId: line.inventoryItemId,
      }),
    );
    setItems((prev) => [...prev, ...cloned]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supplierId || items.length === 0) {
      alert('Select a supplier and add at least one line.');
      return;
    }
    const invalid = items.some((l) => !l.productName.trim());
    if (invalid) {
      alert('Every line needs a product name.');
      return;
    }
    setSaving(true);
    try {
      const wasOrdered = po?.status !== 'ordered' && status === 'ordered';
      await onSave({
        id: po?.id ?? '',
        supplierId,
        status,
        items,
        notes: notes.trim() || undefined,
        gstMode,
        paymentTerms: paymentTerms.trim() || undefined,
        deliveryAddress: deliveryAddress.trim() || undefined,
        dateCreated: po?.dateCreated ?? Date.now(),
        dateReceived: po?.dateReceived,
        dateOrdered: wasOrdered ? Date.now() : po?.dateOrdered,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
      <div className="flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {po ? `Edit ${po.id}` : 'New purchase order'}
            </h2>
            <p className="text-sm text-gray-500">
              {items.length} line{items.length === 1 ? '' : 's'} ·{' '}
              <span className="font-semibold text-pink-700">${total.toFixed(2)}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <XCircleIcon className="h-7 w-7" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="space-y-4 overflow-y-auto px-6 py-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Supplier</label>
                <select
                  value={supplierId}
                  onChange={(e) => setSupplierId(parseInt(e.target.value, 10))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  required
                >
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as PurchaseOrderStatus)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm capitalize"
                >
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">GST pricing</label>
                <select
                  value={gstMode}
                  onChange={(e) => setGstMode(e.target.value as PoGstMode)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="inclusive">GST inclusive (unit costs incl. 15%)</option>
                  <option value="exclusive">GST exclusive (15% added on top)</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Payment terms</label>
                <input
                  type="text"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  placeholder="e.g. 20th of month following"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Delivery address
                </label>
                <input
                  type="text"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder={businessInfo?.address || 'Ship-to address'}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Delivery ref, special instructions…"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setEntryMode('quick')}
                className={`rounded-full px-3 py-1 text-sm font-medium ${
                  entryMode === 'quick' ? 'bg-pink-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Quick add
              </button>
              <button
                type="button"
                onClick={() => setEntryMode('paste')}
                className={`rounded-full px-3 py-1 text-sm font-medium ${
                  entryMode === 'paste' ? 'bg-pink-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Paste / CSV
              </button>
              {lastOrderForSupplier?.items.length ? (
                <button
                  type="button"
                  onClick={duplicateLastOrder}
                  className="rounded-full border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Repeat last order
                </button>
              ) : null}
            </div>

            {entryMode === 'paste' ? (
              <POPasteImportPanel
                onImport={(imported) => setItems((prev) => [...prev, ...imported])}
              />
            ) : (
              <div className="relative rounded-lg border border-pink-200 bg-gradient-to-r from-pink-50 to-white p-3">
                <p className="mb-2 text-xs font-medium text-pink-800">
                  Type a product, press Enter — pick from stock or add as new
                </p>
                <div className="grid grid-cols-12 gap-2">
                  <div className="relative col-span-5">
                    <input
                      ref={quickNameRef}
                      type="text"
                      value={quickName}
                      onChange={(e) => {
                        setQuickName(e.target.value);
                        setShowSuggestions(true);
                      }}
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                      onKeyDown={handleQuickKeyDown}
                      placeholder="Product name"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                    {showSuggestions && (quickName || suggestions.length) ? (
                      <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                        {suggestions.map((inv) => (
                          <li key={inv.id}>
                            <button
                              type="button"
                              className="w-full px-3 py-2 text-left text-sm hover:bg-pink-50"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setQuickCost(String(inv.costPerUnit ?? ''));
                                setQuickUnit(inv.unit);
                                commitQuickLine(inv);
                              }}
                            >
                              {inv.name}
                              <span className="ml-2 text-xs text-gray-400">{inv.unit}</span>
                            </button>
                          </li>
                        ))}
                        {quickName.trim() ? (
                          <li>
                            <button
                              type="button"
                              className="w-full px-3 py-2 text-left text-sm font-medium text-pink-700 hover:bg-pink-50"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => commitQuickLine(undefined, quickName.trim())}
                            >
                              + Add new: {quickName.trim()}
                            </button>
                          </li>
                        ) : null}
                      </ul>
                    ) : null}
                  </div>
                  <input
                    type="number"
                    min={1}
                    value={quickQty}
                    onChange={(e) => setQuickQty(e.target.value)}
                    onKeyDown={handleQuickKeyDown}
                    className="col-span-2 rounded-md border border-gray-300 px-2 py-2 text-sm"
                    placeholder="Qty"
                  />
                  <select
                    value={quickUnit}
                    onChange={(e) => setQuickUnit(e.target.value as InventoryItem['unit'])}
                    className="col-span-2 rounded-md border border-gray-300 px-2 py-2 text-sm"
                  >
                    {UNITS.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={quickCost}
                    onChange={(e) => setQuickCost(e.target.value)}
                    onKeyDown={handleQuickKeyDown}
                    className="col-span-2 rounded-md border border-gray-300 px-2 py-2 text-sm"
                    placeholder="Cost"
                  />
                  <button
                    type="button"
                    onClick={() => commitQuickLine()}
                    className="col-span-1 flex items-center justify-center rounded-md bg-pink-600 text-white hover:bg-pink-700"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">Product</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">Unit</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-500">Qty</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-500">Cost</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-500">Line</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                        No lines yet — use quick add or paste above.
                      </td>
                    </tr>
                  ) : (
                    items.map((line, index) => (
                      <tr
                        key={line.lineId}
                        className={
                          highlightLineId === line.lineId
                            ? 'bg-pink-50 transition-colors duration-300'
                            : ''
                        }
                      >
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={line.productName}
                            onChange={(e) => updateLine(index, { productName: e.target.value })}
                            className="w-full rounded border border-gray-200 px-2 py-1"
                          />
                          {!line.inventoryItemId ? (
                            <span className="text-xs text-emerald-600">New product</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={line.unit ?? 'units'}
                            onChange={(e) =>
                              updateLine(index, { unit: e.target.value as InventoryItem['unit'] })
                            }
                            className="rounded border border-gray-200 px-2 py-1"
                          >
                            {UNITS.map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            min={1}
                            value={line.quantity}
                            onChange={(e) =>
                              updateLine(index, { quantity: parseInt(e.target.value, 10) || 1 })
                            }
                            className="w-16 rounded border border-gray-200 px-2 py-1 text-right"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={line.costPerUnit}
                            onChange={(e) =>
                              updateLine(index, { costPerUnit: parseFloat(e.target.value) || 0 })
                            }
                            className="w-20 rounded border border-gray-200 px-2 py-1 text-right"
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-medium tabular-nums">
                          ${(line.quantity * line.costPerUnit).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => removeLine(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="border-t bg-gray-50 px-6 py-4">
            <p className="mb-2 text-center text-xs text-gray-500">
              Enter = add line · Esc = close · Mark <strong>ordered</strong> then print PO for supplier
            </p>
            <div className="mb-3 space-y-1 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal (ex. GST)</span>
                <span>${totals.subtotalExGst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>GST (15%)</span>
                <span>${totals.gstAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-gray-900">
                <span>Total (inc. GST)</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              {canPrintPo && po?.id && businessInfo ? (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handlePrintPo}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-white"
                  >
                    Download PO PDF
                  </button>
                  <button
                    type="button"
                    onClick={handleEmailPo}
                    className="rounded-md border border-pink-300 px-3 py-1.5 text-sm text-pink-700 hover:bg-pink-50"
                  >
                    Email supplier
                  </button>
                </div>
              ) : (
                <span className="text-xs text-gray-500">
                  Save as <strong>ordered</strong> to print or email PO
                </span>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-md bg-pink-600 px-5 py-2 font-medium text-white hover:bg-pink-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save order'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurchaseOrderBuilder;
